import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const UserDirectory = ({ users, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.user_profiles?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.user_profiles?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && user?.is_active) ||
                         (filterStatus === 'inactive' && !user?.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadgeColor = (role) => {
    const colors = {
      gym_owner: 'bg-primary/10 text-primary',
      trainer: 'bg-accent/10 text-accent',
      staff: 'bg-warning/10 text-warning',
      member: 'bg-success/10 text-success'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  const getPlanBadgeColor = (plan) => {
    const colors = {
      basic: 'bg-muted text-muted-foreground',
      premium: 'bg-primary/10 text-primary',
      elite: 'bg-accent/10 text-accent'
    };
    return colors?.[plan] || 'bg-muted text-muted-foreground';
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    setLoading(true);
    try {
      const { error } = await supabase?.from('tenant_members')?.update({ is_active: !currentStatus })?.eq('id', userId);

      if (error) throw error;
      onRefresh?.();
    } catch (err) {
      console.error('Failed to update user status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setLoading(true);
    try {
      const { error } = await supabase?.from('tenant_members')?.update({ role: newRole })?.eq('id', userId);

      if (error) throw error;
      onRefresh?.();
    } catch (err) {
      console.error('Failed to update user role:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
          <Icon name="Users" size={24} color="var(--color-primary)" />
          User Directory
        </h2>
        <Button onClick={onRefresh} variant="ghost" size="sm" disabled={loading}>
          <Icon name="RefreshCw" size={18} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="relative">
          <Icon name="Search" size={18} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          />
        </div>
        <div className="flex gap-3">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e?.target?.value)}
            className="flex-1 px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          >
            <option value="all">All Roles</option>
            <option value="gym_owner">Gym Owner</option>
            <option value="trainer">Trainer</option>
            <option value="staff">Staff</option>
            <option value="member">Member</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="flex-1 px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {filteredUsers?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No users found</p>
          </div>
        ) : (
          filteredUsers?.map((user) => (
            <div
              key={user?.id}
              className="flex items-center justify-between p-4 bg-background rounded-lg border border-border hover:border-primary transition-smooth"
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Icon name="User" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">
                    {user?.user_profiles?.full_name || 'Unknown'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {user?.user_profiles?.email}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <select
                      value={user?.role}
                      onChange={(e) => handleRoleChange(user?.id, e?.target?.value)}
                      disabled={loading}
                      className={`text-xs px-2 py-1 rounded-full font-medium border-0 ${getRoleBadgeColor(user?.role)}`}
                    >
                      <option value="gym_owner">Gym Owner</option>
                      <option value="trainer">Trainer</option>
                      <option value="staff">Staff</option>
                      <option value="member">Member</option>
                    </select>
                    {user?.membership_plan && (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPlanBadgeColor(user?.membership_plan)}`}>
                        {user?.membership_plan}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleToggleStatus(user?.id, user?.is_active)}
                  disabled={loading}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-smooth ${
                    user?.is_active
                      ? 'bg-success/10 text-success hover:bg-success/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {user?.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UserDirectory;